import { supabase } from "@/integrations/supabase/client";
import type { ReportCertificateReadModel } from "@/lib/reportCertificateEvidence";
import {
  buildFunctionInvokeError,
  FunctionInvokeError,
} from "@/lib/functionInvokeErrors";
import {
  getProtectedFunctionHeaders,
  invokeProtectedFunction,
} from "@/lib/protectedFunctionAuth";
import {
  isTransientBackendError,
  TRANSIENT_READ_MAX_RETRIES,
} from "@/lib/transientBackendError";
import type {
  Alert as DashboardAlert,
  CertificationType,
  DashboardStats,
  Organization,
  OrganizationMember,
  Supplier,
  SupplierCertification,
} from "@/types";
import type {
  PoamPagination,
  PoamRecord,
  PoamSeverity,
  PoamStatus,
  PoamSupplierSummary,
} from "@/types/poam";
import type {
  DomainSubCheck,
  DomainVulnerability,
  EmailSubCheck,
  SupplierSecurityDetails,
} from "@/lib/insightsVulnerabilityModel";
import type { SupplyChainBriefingReport } from "@/lib/supplyChainBriefingGenerator";

const MARKETPLACE_LISTING_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * ENT-1814 (ENT-1745 L2, Row 2): the SINGLE resolver for "which marketplace listing does this
 * supplier reference?". Prefers the typed `suppliers.marketplace_listing_id`; falls back to the
 * DEPRECATED, overloaded `suppliers.vendor_id` ONLY when it is uuid-shaped (a legacy listing
 * id), never when it is a free customer vendor code. Returns null when neither yields a listing
 * id. Every read site must use this so the fallback logic lives in one place during the
 * dual-write transition.
 */
export function resolveMarketplaceListingId(
  supplier:
    | { marketplace_listing_id?: string | null; vendor_id?: string | null }
    | null
    | undefined,
): string | null {
  const direct = supplier?.marketplace_listing_id;
  if (typeof direct === "string" && direct.trim()) {
    return direct.trim();
  }
  const legacy = supplier?.vendor_id;
  if (typeof legacy === "string" && MARKETPLACE_LISTING_UUID_RE.test(legacy.trim())) {
    return legacy.trim();
  }
  return null;
}

export interface SuppliersReadModelFilters {
  criticality: string;
  status: string;
  questionnaireAccess: boolean;
  hasCertificates: boolean;
  supplierType: string;
}

export interface SuppliersReadModelRequest {
  organizationId: string;
  page: number;
  pageSize: number;
  searchQuery: string;
  nameSortDir: "asc" | "desc";
  departmentFilter: string;
  selectedCategory: string | null;
  filters: SuppliersReadModelFilters;
}

export interface SuppliersReadModelResponse {
  suppliers: Supplier[];
  totalCount: number;
  organizationTotalCount: number;
  supplierTypes: string[];
  supplierDeptMap: Record<string, string[]>;
  tierSummary: Array<{
    criticality_tier: string;
    supplier_count: number;
    avg_score: number | null;
  }>;
}

export interface DependenciesReadModelRequest {
  organizationId: string;
  departmentFilter: string;
  minCount: number;
}

export interface DependenciesSupplierRow {
  id: string;
  name: string;
  /** @deprecated ENT-1970 — prefer marketplace_listing_id; vendor_id is the transition fallback. */
  vendor_id: string | null;
  /**
   * Typed FK link. ENT-2954 (ENT-1990 reader cutover) moved `get_dependencies_read_model`'s joins
   * onto `marketplace_listing_id` AND now emits it in this projection, so `resolveMarketplaceListingId`
   * prefers the typed FK and associates typed-only suppliers (vendor_id NULL) with their topology;
   * `vendor_id` remains the transition fallback for any legacy row that lacks the FK.
   */
  marketplace_listing_id?: string | null;
}

export interface DependenciesListingRow {
  id: string;
  discovery_data: unknown;
  logo_url: string | null;
}

export interface DependenciesFingerprintRow {
  id: string;
  provider_name: string;
  provider_type: string;
  provider_category: string | null;
  confidence_score: number | null;
  detection_source: string | null;
  first_detected_at: string | null;
  marketplace_listing_id: string;
}

export interface DependenciesReadModelResponse {
  suppliers: DependenciesSupplierRow[];
  listings: DependenciesListingRow[];
  fingerprints: DependenciesFingerprintRow[];
  stats: {
    total_fingerprints: number;
    unique_providers: number;
    concentration_risks: Array<{
      provider_name: string;
      provider_type: string;
      supplier_count: number;
      supplier_ids: string[];
      supplier_names: string[];
    }>;
    by_type: Record<string, number>;
  };
}

export interface PoamReadModelRequest {
  organizationId: string;
  page?: number;
  pageSize?: number;
  filters?: {
    severity?: string[];
    status?: string[];
    riskCategory?: string[];
    sourceType?: string[];
    supplierId?: string;
    ownerId?: string;
    overdue?: boolean;
    search?: string;
    sortByDueDate?: "asc" | "desc";
    sortByOwner?: "asc" | "desc";
  };
}

export interface PoamReadModelResponse {
  records: PoamRecord[];
  groupSummaries: PoamSupplierSummary[];
  pagination: PoamPagination;
  stats: {
    total: number;
    bySeverity: Record<PoamSeverity, number>;
    byStatus: Record<PoamStatus, number>;
    overdue: number;
    avgResolutionDays: number | null;
  };
}

export interface DashboardReadModelRequest {
  organizationId: string;
}

export interface NavigatorHomeReadModelRequest {
  organizationId: string;
}

export interface NavigatorSectionSummariesReadModelRequest {
  organizationId: string;
}

export interface NavigatorSupplyChainBriefingReadModelRequest {
  organizationId: string;
  idempotencyKey?: string;
  pdfBase64?: string;
  snapshotId?: string;
  artifactType?: "html" | "pdf";
}

export interface NavigatorSupplyChainBriefingSnapshotArtifactUrlResponse {
  url: string;
  expiresInSeconds: number;
  artifactType: "html" | "pdf";
  fileName: string;
}

export type NavigatorSupplyChainBriefingReadModelResponse = SupplyChainBriefingReport;

export interface NavigatorSupplyChainBriefingSnapshotSummary {
  id: string;
  capturedAt: string;
  generatedAt: string;
  snapshotVersion: number;
  clientName: string;
  title: string;
  supplierCount: number;
  evidenceDataPointCount: number;
  overallRiskLevel: string;
  createdBy: string | null;
}

export interface NavigatorSupplyChainBriefingSnapshotListResponse {
  snapshots: NavigatorSupplyChainBriefingSnapshotSummary[];
}

export interface NavigatorSupplyChainBriefingSnapshotResponse {
  snapshot: NavigatorSupplyChainBriefingSnapshotSummary;
  report: SupplyChainBriefingReport;
}

export interface NavigatorSectionSummariesReadModelResponse {
  foundation: NavigatorFoundationReadModelResponse["summary"];
  crownJewels: NavigatorCrownJewelsReadModelResponse["summary"];
  suppliers: NavigatorSuppliersReadModelResponse["summary"];
  contracts: NavigatorContractsReadModelResponse["summary"];
}

export interface NavigatorHomeReadModelResponse {
  summary: {
    programmeStatus: "not_started" | "in_progress" | "needs_attention" | "healthy";
    programmeHealthScore: number;
    totalSuppliers: number;
    certificationCoverage: number;
    openActions: number;
    overdueActions: number;
    highRiskSuppliers: number;
    upcomingReviews: number;
    blockerCount: number;
  };
  foundation: {
    status: "not_started" | "in_progress" | "complete";
    gapCount: number;
    completionCount: number;
    totalPanels: number;
    nextReviewLabel: string | null;
    unresolvedGaps: Array<{
      id: string;
      title: string;
      description: string;
    }>;
  };
  crownJewels: {
    status: "not_started" | "in_progress" | "complete";
    gapCount: number;
    totalCount: number;
    mappedCount: number;
    criticalCount: number;
  };
  suppliers: {
    status: "not_started" | "in_progress" | "needs_attention" | "complete";
    readinessScore: number;
    blockerCount: number;
    totalSuppliers: number;
    unclassifiedSuppliers: number;
    mappedToCrownJewelsCount: number;
    missingCoreDetailsCount: number;
    reassessmentDueCount: number;
  };
  priorities: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    severity: "critical" | "high" | "medium";
    detail?: {
      type: "overdue_poam";
      poamId: string;
      itemTitle: string;
      supplierName: string | null;
      dueDate: string | null;
      severity: string;
      status: string;
      riskStatement: string | null;
      recommendedAction: string | null;
    };
  }>;
  postureChanges: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    sourceLabel: string;
    detectedAt: string | null;
    severity: "critical" | "high" | "medium";
  }>;
  nextActions: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    priority: "high" | "medium" | "low";
  }>;
  actionQueue: Array<{
    id: string;
    title: string;
    subtitle: string;
    description: string;
    sourceLabel: string;
    group: "urgent" | "this_week" | "watch" | "clear";
    severity: "critical" | "high" | "medium" | "low";
    kind: "priority" | "posture_change" | "next_action" | "theme";
    href: string;
    supportingSignals: string[];
    whyNow: string;
    ifIgnored: string;
    recommendedOwner: string;
    focusSignals: Array<{
      label: string;
      value: string;
      hint: string;
      tone: "rose" | "amber" | "emerald" | "slate" | "sky";
    }>;
    steps: Array<{
      title: string;
      description: string;
      tone: "urgent" | "soon" | "conditional";
      href: string;
      ctaLabel: string;
    }>;
  }>;
  clearSections: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
  }>;
}

export interface NavigatorCrownJewelsReadModelRequest {
  organizationId: string;
}

export interface NavigatorCrownJewelsReadModelResponse {
  summary: {
    overallStatus: "not_started" | "in_progress" | "complete";
    gapCount: number;
    totalCount: number;
    mappedCount: number;
    criticalCount: number;
    editable: boolean;
  };
  records: Array<{
    id: string;
    name: string;
    jewelType: "service" | "system" | "data" | "process";
    businessFunction: string;
    description: string;
    businessOwnerRole: string;
    criticalityTier: "critical" | "high" | "medium" | "low" | "";
    criticalityRationale: string;
    dataSensitivity: string;
    operationalDependencySummary: string;
    supplierIds: string[];
  }>;
  supplierOptions: Array<{
    id: string;
    name: string;
    criticalityTier: string | null;
  }>;
  ownerOptions: Array<{
    value: string;
    label: string;
    role: "executive_sponsor" | "programme_lead" | "stakeholder";
  }>;
  unresolvedGaps: Array<{
    id: string;
    title: string;
    description: string;
  }>;
}

export interface NavigatorSuppliersReadModelRequest {
  organizationId: string;
}

export interface NavigatorSuppliersReadModelResponse {
  summary: {
    overallStatus: "not_started" | "in_progress" | "needs_attention" | "complete";
    mode: "setup" | "gap_analysis";
    readinessScore: number;
    totalSuppliers: number;
    classifiedSuppliers: number;
    unclassifiedSuppliers: number;
    mappedToCrownJewelsCount: number;
    higherImpactSuppliersNeedingLinkageCount: number;
    higherImpactSuppliersLinkedCount: number;
    missingCoreDetailsCount: number;
    evidenceGapCount: number;
    classificationReviewAttentionCount: number;
    reassessmentDueCount: number;
    reassessmentRecommendedCount: number;
    blockerCount: number;
    editable: boolean;
  };
  setupGuidance: {
    crownJewelsTotal: number;
    criticalCrownJewels: number;
    suggestedStartingPoints: Array<{
      id: string;
      name: string;
      businessFunction: string;
      criticalityTier: "critical" | "high" | "medium" | "low" | "";
      href: string;
    }>;
    steps: Array<{
      id: string;
      title: string;
      description: string;
      href: string;
      ctaLabel: string;
    }>;
  };
  portfolioGaps: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    severity: "critical" | "high" | "medium";
  }>;
  supplierGaps: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    supplierLogoUrl: string | null;
    status: string;
    criticalityTier: "critical" | "high" | "medium" | "low" | "unclassified";
    readinessScore: number;
    priority: "critical" | "high" | "medium";
    auditReadinessStatus: "blocked" | "review_needed" | "ready";
    crownJewelLinked: boolean;
    classificationFreshness: "missing" | "current" | "review_due";
    reassessmentStatus: "not_needed" | "recommended" | "due" | "evidence_gap";
    reassessmentReasons: string[];
    missingFields: string[];
    href: string;
  }>;
  reassessmentQueue: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    criticalityTier: "critical" | "high" | "medium" | "low" | "unclassified";
    reassessmentStatus: "recommended" | "due" | "evidence_gap";
    reassessmentReasons: string[];
    href: string;
  }>;
  nextActions: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    priority: "high" | "medium" | "low";
  }>;
  unclassifiedQueue: Array<{
    id: string;
    name: string;
    logoUrl: string | null;
  }>;
}

export interface NavigatorContractsReadModelRequest {
  organizationId: string;
}

export interface NavigatorContractsReadModelResponse {
  summary: {
    overallStatus: "not_started" | "in_progress" | "needs_attention" | "complete";
    readinessScore: number;
    totalSuppliers: number;
    inScopeSuppliers: number;
    reviewedSuppliers: number;
    contractRecordCount: number;
    obligationGapCount: number;
    overdueReviewCount: number;
    exceptionCount: number;
    blockerCount: number;
    requiredSuppliers: number;
    recommendedSuppliers: number;
    outOfScopeSuppliers: number;
    editable: boolean;
  };
  scopeDecisions: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    supplierLogoUrl: string | null;
    status: string;
    criticalityTier: "critical" | "high" | "medium" | "low" | "unclassified";
    dataAccessLevel: string | null;
    supplierType: string | null;
    crownJewelLinked: boolean;
    contractCount: number;
    reviewDecision: "required" | "recommended" | "not_required";
    requirementTier: "baseline" | "enhanced" | "critical" | null;
    rationale: string[];
    plainLanguageSummary: string;
    nextReviewLabel: string | null;
    href: string;
  }>;
  requirementProfiles: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    supplierLogoUrl: string | null;
    criticalityTier: "critical" | "high" | "medium" | "low" | "unclassified";
    reviewDecision: "required" | "recommended";
    requirementTier: "baseline" | "enhanced" | "critical";
    rationale: string[];
    requirementCoverage: Array<{
      key: "security-schedule" | "incident-reporting" | "audit-rights" | "subcontractor-controls" | "exit-offboarding";
      title: string;
      category: "controls" | "evidence" | "contractual";
      requirementTier: "baseline" | "enhanced" | "critical";
      whyItMatters: string;
      whatToInclude: string;
      evidenceExpected: string;
      frameworks: string[];
      status: "missing" | "partial" | "covered" | "excepted";
      currentEvidence: string | null;
      reviewAction: string;
      exception: null | {
        reason: string;
        compensatingControl: string | null;
        reviewDueLabel: string | null;
      };
    }>;
    coverageSummary: {
      coveredCount: number;
      partialCount: number;
      missingCount: number;
      exceptedCount: number;
    };
    contractPosture: "missing" | "partial" | "covered";
    nextReviewLabel: string | null;
    href: string;
  }>;
  contractGaps: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    supplierLogoUrl: string | null;
    priority: "critical" | "high" | "medium";
    title: string;
    description: string;
    requirementKey: "security-schedule" | "incident-reporting" | "audit-rights" | "subcontractor-controls" | "exit-offboarding";
    requirementTitle: string;
    reviewAction: string;
    status: "missing" | "partial" | "covered" | "excepted";
    href: string;
  }>;
  exceptions: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    supplierLogoUrl: string | null;
    requirementKey: "security-schedule" | "incident-reporting" | "audit-rights" | "subcontractor-controls" | "exit-offboarding";
    requirementTitle: string;
    reason: string;
    compensatingControl: string | null;
    reviewDueLabel: string | null;
    href: string;
  }>;
  reviewsDue: Array<{
    id: string;
    supplierId: string;
    supplierName: string;
    reason: string;
    priority: "high" | "medium";
    dueLabel: string | null;
    href: string;
  }>;
  nextActions: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    priority: "high" | "medium" | "low";
  }>;
}

export interface NavigatorCrownJewelsUpsertRequest {
  organizationId: string;
  records: Array<{
    id: string;
    name: string;
    jewelType: string;
    businessFunction: string;
    description?: string;
    businessOwnerRole?: string;
    criticalityTier?: string | null;
    criticalityRationale?: string;
    dataSensitivity?: string;
    operationalDependencySummary?: string;
    supplierIds?: string[];
  }>;
}

export interface NavigatorCrownJewelsUpsertResponse {
  ok: boolean;
  recordsSaved: number;
}

export interface NavigatorCrownJewelLinkageUpsertRequest {
  organizationId: string;
  supplierId: string;
  crownJewelId: string;
}

export interface NavigatorCrownJewelLinkageUpsertResponse {
  ok: boolean;
  crownJewelId: string;
  supplierId: string;
}

export interface NavigatorFoundationReadModelRequest {
  organizationId: string;
}

export interface NavigatorFoundationReadModelResponse {
  summary: {
    overallStatus: "not_started" | "in_progress" | "complete";
    gapCount: number;
    completionCount: number;
    totalPanels: number;
    nextReviewLabel: string | null;
    editable: boolean;
  };
  panels: {
    executiveSponsor: {
      status: "not_started" | "in_progress" | "complete";
      members: Array<{
        name: string;
        position: string;
      }>;
    };
    programmeLead: {
      status: "not_started" | "in_progress" | "complete";
      members: Array<{
        name: string;
        position: string;
      }>;
    };
    workingGroup: {
      status: "not_started" | "in_progress" | "complete";
      noneSelected?: boolean;
      members: Array<{
        id: string;
        name: string;
        position: string;
      }>;
    };
    charter: {
      status: "not_started" | "in_progress" | "complete";
      summary: string;
    };
    riskAppetite: {
      status: "not_started" | "in_progress" | "complete";
      summary: string;
      statement: string;
      generatedAt: string | null;
      answers: null | {
        q11: string[];
        q12: string;
        q21: string[];
        q22: string;
        q31: string;
        q32: string;
        q41: string;
        q42: string;
        q51: string;
        q52: string;
        q61: string;
        q62: string;
      };
    };
    reportingAndSignOff: {
      status: "not_started" | "in_progress" | "complete";
      reportingCadence: string;
      signOffModelSummary: string;
    };
  };
  unresolvedGaps: Array<{
    id: string;
    title: string;
    description: string;
  }>;
}

export interface NavigatorFoundationUpsertRequest {
  organizationId: string;
  executiveSponsors: Array<{
    id?: string;
    name?: string;
    position?: string;
  }>;
  programmeLeads: Array<{
    id?: string;
    name?: string;
    position?: string;
  }>;
  workingGroup: Array<{
    id?: string;
    name?: string;
    position?: string;
  }>;
  stakeholdersNone?: boolean;
  charterSummary?: string;
  riskAppetiteSummary?: string;
  riskAppetiteAnswers?: {
    q11: string[];
    q12: string;
    q21: string[];
    q22: string;
    q31: string;
    q32: string;
    q41: string;
    q42: string;
    q51: string;
    q52: string;
    q61: string;
    q62: string;
  } | null;
  reportingCadence?: string;
  signOffModelSummary?: string;
}

export interface NavigatorFoundationUpsertResponse {
  ok: boolean;
  foundationId: string;
}

export interface NavigatorCharterDocumentSection {
  id:
    | "purpose"
    | "objectives"
    | "scope"
    | "governance_and_authority"
    | "roles_and_responsibilities"
    | "key_activities"
    | "risk_management"
    | "success_metrics"
    | "review";
  title: string;
  content: string;
}

export interface NavigatorCharterDocument {
  schemaVersion: "navigator-charter-v1";
  title: string;
  metadata: {
    programmeName: string;
    sponsor: string;
    programmeOwner: string;
    approvalDate: string;
    versionLabel: string;
  };
  sections: NavigatorCharterDocumentSection[];
}

export interface NavigatorCharterReadModelRequest {
  organizationId: string;
}

export interface NavigatorCharterReadModelResponse {
  summary: {
    status: "not_started" | "draft" | "published";
    editable: boolean;
    canGenerate: boolean;
    currentVersionLabel: string | null;
    publishedVersionCount: number;
    latestPublishedAt: string | null;
  };
  document: null | {
    id: string;
    status: "draft" | "published";
    title: string;
    currentVersionLabel: string;
    generatedFromTemplateVersion: string;
    updatedAt: string;
    publishedAt: string | null;
    document: NavigatorCharterDocument;
  };
  versionHistory: Array<{
    id: string;
    versionNumber: number;
    versionLabel: string;
    publishedAt: string;
    pdfStoragePath: string | null;
    document: NavigatorCharterDocument | null;
  }>;
}

export interface NavigatorCharterGenerateRequest {
  organizationId: string;
}

export interface NavigatorCharterGenerateResponse {
  ok: boolean;
  created: boolean;
  documentId: string;
  mode?: "created" | "existing_draft" | "draft_from_published";
}

export interface NavigatorCharterSaveDraftRequest {
  organizationId: string;
  documentId: string;
  title?: string;
  document: NavigatorCharterDocument;
}

export interface NavigatorCharterSaveDraftResponse {
  ok: boolean;
  documentId: string;
  currentVersionLabel: string;
}

export interface NavigatorCharterPublishRequest {
  organizationId: string;
  documentId: string;
}

export interface NavigatorCharterPublishResponse {
  ok: boolean;
  documentId: string;
  versionId: string;
  versionLabel: string;
  publishedAt: string;
}

export interface DashboardSummaryReadModelResponse {
  stats: DashboardStats;
}

export interface DashboardDeferredReadModelResponse {
  recentAlerts: DashboardAlert[];
  recentSuppliers: Supplier[];
  breachArticles: Array<{
    title: string;
    url: string;
    description: string;
    matched_supplier: string;
    marketplace_listing_id: string;
    published_at: string;
    source_name: string;
    image_url: string | null;
    relevance_score?: number;
    ai_analysis?: string;
  }>;
}

export interface DashboardReadModelResponse
  extends
    DashboardSummaryReadModelResponse,
    DashboardDeferredReadModelResponse {
  activeSupplierNames: string[];
}

export interface MspCustomersDashboardRequest {
  organizationId: string;
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  selectedLetter?: string | null;
  selectedTeamMember?: string | null;
  sortField?: string | null;
  sortDirection?: "asc" | "desc" | null;
}

export interface MspCustomersDashboardResponse {
  summary: {
    customers: number;
    suppliers: number | null;
    overdueActions: number | null;
  };
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  rows: Array<Record<string, unknown>>;
}

export interface MspInsightsDashboardRequest {
  organizationId: string;
  customerOrgId?: string | null;
  section?: "summary" | "trend";
  dateRange?: "1m" | "3m" | "6m" | "12m";
}

export interface MspInsightsDashboardResponse {
  summary?: {
    customer_count: number;
    total_suppliers: number;
    average_risk_score: number | null;
    open_poams: number;
    overdue_poams: number;
    certification_coverage: number;
    questionnaire_access_coverage: number;
    avg_resolution_days: number | null;
    suppliers_by_tier: Array<Record<string, unknown>>;
    poams_by_status: Array<Record<string, unknown>>;
  };
  trend?: Array<{
    snapshot_date: string;
    supplier_count: number;
    avg_score: number | null;
    cert_coverage_pct: number | null;
    quest_coverage_pct: number | null;
  }>;
}

export interface AppShellReadModelRequest {
  organizationId: string;
  agentOrganizationId?: string;
}

export interface AppShellReadModelResponse {
  activeAlertCount: number;
  unreadNotificationCount: number;
  currentUserProfile: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export interface SuperAdminDebugOverlayReadModelRequest {
  organizationId?: string | null;
  surface: "admin" | "customer" | "msp" | "vendor";
  pathname: string;
  enabledAt: string;
}

export interface SuperAdminDebugOverlayReadModelResponse {
  surface: "admin" | "customer" | "msp" | "vendor";
  pathname: string;
  generatedAt: string;
  audit: {
    last30Minutes: number;
    recentEvents: Array<{
      id: string;
      description: string;
      actionType: string;
      actionCategory: string;
      createdAt: string;
      actorName: string | null;
    }>;
  };
  deliveries: {
    notificationsLast30Minutes: number;
    alertsLast30Minutes: number;
    recentDeliveries: Array<{
      id: string;
      kind: string;
      type: string;
      status: string;
      title: string;
      createdAt: string;
    }>;
  };
  apiBudget: {
    providers: Array<{
      provider: string;
      creditsUsed: number;
      creditsLimit: number;
      requestsCount: number;
      lastRequestAt: string | null;
    }>;
    aiAgent: {
      enabled: boolean;
      tokensUsedThisMonth: number;
      monthlyTokenLimit: number;
      tokenResetAt: string | null;
    } | null;
  };
}

export interface AgentFabReadModelRequest {
  organizationId: string;
  agentOrganizationId?: string;
}

export interface AgentFabReadModelResponse {
  agentEnabled: boolean;
}

export interface AdminPlatformInsight {
  type: string;
  shortType: string;
  count: number;
  avgScore: number;
  minScore: number;
  maxScore: number;
  highRiskCount: number;
}

export interface AdminPlatformInsightsResponse {
  totalListings: number;
  platformAverageScore: number;
  unclassifiedCount: number;
  // ENT-5449: active listings with no canonical SupplierLens score. They are in
  // totalListings but in no sector row.
  unscoredCount: number;
  insights: AdminPlatformInsight[];
}

export type AdminMarketplaceDependenciesSortKey =
  | "supplier_count"
  | "provider_name"
  | "provider_type";
export type AdminMarketplaceDependenciesSortDirection = "asc" | "desc";

export interface AdminMarketplaceDependenciesRequest {
  page: number;
  pageSize: number;
  searchQuery: string;
  providerType: string;
  sortKey: AdminMarketplaceDependenciesSortKey;
  sortDirection: AdminMarketplaceDependenciesSortDirection;
  /** ENT-6287: only providers with at least one unlinked row. */
  unresolvedOnly?: boolean;
}

/** ENT-6287: how `supplier_infrastructure.provider_listing_id` was resolved. */
export type ProviderLinkMethod = "catalog_domain" | "raw_domain" | "name_exact" | "manual";
/** ENT-6287: why a provider is not linked. */
export type ProviderLinkReason =
  | "ambiguous"
  | "no_listing"
  | "wildcard_pattern"
  | "self"
  | "listing_deleted"
  | "listing_renamed";

export interface ProviderLinkCandidate {
  id: string;
  company_name: string | null;
  domain: string | null;
}

export interface AdminMarketplaceDependenciesItem {
  provider_name: string;
  provider_type: string;
  supplier_count: number;
  /** The linked listing's domain/status (kept for older clients). */
  exact_marketplace_domain: string | null;
  exact_marketplace_status: string | null;
  raw_values: string[];
  linked_listing?: {
    id: string;
    company_name: string | null;
    domain: string | null;
    status: string | null;
  } | null;
  link_method?: ProviderLinkMethod | null;
  link_reason?: ProviderLinkReason | null;
  candidates?: ProviderLinkCandidate[];
  linked_rows?: number;
  unlinked_rows?: number;
  linked_at?: string | null;
  catalog?: {
    canonical_domain: string;
    source: "fingerprint_catalog" | "ent5690_alias_seed" | "manual" | "ent5253_merge";
    set_by: string | null;
    updated_at: string | null;
  } | null;
}

export interface AdminProviderCatalogUpsertRequest {
  providerName: string;
  canonicalDomain: string;
}

export interface AdminProviderCatalogUpsertResponse {
  provider_name: string;
  canonical_domain: string;
  listing_id: string;
  linked_rows: number;
}

export interface AdminMarketplaceDependenciesResponse {
  items: AdminMarketplaceDependenciesItem[];
  totalCount: number;
  providerTypes: string[];
  page: number;
  pageSize: number;
}

export type AdminCronMonitoringStatus = "green" | "amber" | "red" | "unknown";
export type AdminCronAnalysisStatus =
  | "fresh"
  | "stale"
  | "unavailable"
  | "error"
  | "not_requested";

export interface AdminCronIssueSummary {
  code: string;
  severity: "info" | "warning" | "error";
  title: string;
  detail: string;
}

export interface AdminCronJobSummary {
  jobId: number;
  jobKey: string;
  jobName: string;
  active?: boolean;
  schedule: string;
  command: string;
  wrapperName: string | null;
  functionName: string | null;
  status: AdminCronMonitoringStatus;
  latestRunAt: string | null;
  latestRunStatus: string | null;
  latestRunDurationMs: number | null;
  recentFailureCount: number;
  issueSummary: AdminCronIssueSummary[];
  analysisStatus: AdminCronAnalysisStatus;
  analysisGeneratedAt: string | null;
  analysisExpiresAt: string | null;
}

export interface AdminCronMonitoringSummaryResponse {
  generatedAt: string;
  analysisCapability: {
    enabled: boolean;
    reason: string | null;
  };
  counts: Record<AdminCronMonitoringStatus, number>;
  jobs: AdminCronJobSummary[];
}

export interface AdminCronLogEvent {
  observedAt: string;
  sourceType: string;
  severity: "info" | "warning" | "error";
  requestPath: string | null;
  requestMethod: string | null;
  statusCode: number | null;
  eventMessage: string;
  eventFingerprint: string;
  rawPayload: Record<string, unknown>;
}

export interface AdminCronMonitoringDetailResponse {
  summary: AdminCronJobSummary;
  recentRuns: Array<{
    runId: number;
    status: string;
    returnMessage: string | null;
    startedAt: string;
    endedAt: string | null;
    durationMs: number | null;
  }>;
  analysis: {
    status: AdminCronAnalysisStatus;
    generatedAt: string | null;
    expiresAt: string | null;
    lastError: string | null;
    issues: AdminCronIssueSummary[];
    logs: AdminCronLogEvent[];
  };
}

export type AdminThirdPartyApiBudgetMode = 'off' | 'log_only' | 'enforce';
export type AdminThirdPartyApiBudgetWindow = 'minute' | 'hour' | 'day' | 'week' | 'month';
export type AdminThirdPartyApiBudgetMetricType = 'requests' | 'tokens';

export interface AdminThirdPartyApiBudgetProviderState {
  provider:
    | 'apivoid'
    | 'breach_directory'
    | 'companies_house'
    | 'firecrawl'
    | 'gnews'
    | 'hibp'
    | 'leakradar'
    | 'ransomware_live'
    | 'openai';
  consumerKey: 'marketing_generate' | 'score_questionnaire' | 'supplier_classification' | null;
  label: string;
  enabled: boolean;
  mode: AdminThirdPartyApiBudgetMode;
  window: AdminThirdPartyApiBudgetWindow;
  metricType: AdminThirdPartyApiBudgetMetricType;
  unitLabel: 'calls' | 'tokens';
  limitValue: number;
  consumedUnits: number;
  reservedUnits: number;
  maxCalls: number;
  callsUsed: number;
  remaining: number;
  windowStart: string | null;
  windowEnd: string | null;
  lastCheckedAt: string | null;
  lastRequestAt: string | null;
  lastBlockedAt: string | null;
  lastSourceFunction: string | null;
  lastDecision: 'allowed' | 'blocked' | null;
  vendorLimit: number | null;
  vendorRemaining: number | null;
  vendorResetAt: string | null;
  vendorRetryAfterSeconds: number | null;
  lastStatusCode: number | null;
}

export interface AdminThirdPartyApiBudgetsResponse {
  settingKey: string;
  mode: AdminThirdPartyApiBudgetMode;
  updatedAt: string | null;
  providers: AdminThirdPartyApiBudgetProviderState[];
}

export interface BootstrapSubscriptionReadModelResponse {
  subscribed: boolean;
  billingType: "stripe" | "manual" | "grandfathered" | null;
  subscriptionStatus: string | null;
  planSlug: string | null;
  planName: string | null;
  billingInterval: "monthly" | "annual" | null;
  subscriptionEnd: string | null;
  canceledAt?: string | null;
  commitmentStartedAt?: string | null;
  commitmentEndsAt?: string | null;
}

export interface AppBootstrapReadModelRequest {
  currentOrgId?: string | null;
  isMspContext?: boolean;
  mspHomeOrgId?: string | null;
}

export interface BootstrapSupplierLimitReadModelResponse {
  currentCount: number;
  maxSuppliers: number | null;
  isMSPManaged: boolean;
  mspOrgName: string | null;
}

export interface AppBootstrapReadModelResponse {
  organization: Organization | null;
  membership: OrganizationMember | null;
  isMspOrg: boolean;
  isMspContext: boolean;
  mspHomeOrgId: string | null;
  subscription: BootstrapSubscriptionReadModelResponse | null;
  supplierLimit: BootstrapSupplierLimitReadModelResponse | null;
}

export interface OrganizationContextReadModelRequest {
  currentOrgId?: string | null;
  isMspContext?: boolean;
  mspHomeOrgId?: string | null;
}

export interface OrganizationContextReadModelResponse {
  organization: Organization | null;
  membership: OrganizationMember | null;
  organizations: Organization[];
  mspCustomerOrgs: Organization[];
  departments: Array<{
    id: string;
    organization_id: string;
    name: string;
    is_custom: boolean;
    display_order: number;
    created_at: string;
  }>;
  isMspOrg: boolean;
  isMspContext: boolean;
  mspHomeOrgId: string | null;
}

export interface InsightsReadModelRequest {
  organizationId: string;
  tierFilter: string;
  typeFilter: string;
  certificationTypeFilter?: string;
  statusFilter: string;
  departmentFilter: string;
  dateRange: "1m" | "3m" | "6m" | "12m";
  section?: "overview" | "vulnerabilities" | "questionnaires" | "customQuestionnaires" | "certifications" | "reportCertificates";
  questionnaireBlueprintId?: string | null;
  customQuestionnaireVersionId?: string | null;
  customQuestionnaireSearch?: string | null;
  customQuestionnairePage?: number;
  customQuestionnairePageSize?: number;
  reportCriticalityTiers?: string[];
}

export interface InsightsCertificationSupplier {
  supplierId: string;
  supplierName: string;
  marketplaceListingId: string | null;
  websiteUrl: string | null;
  criticalityTier: string | null;
  supplierType: string | null;
  logoUrl: string | null;
  status: "ready" | "processing" | "failed" | "review_needed" | "expired" | "missing";
  provenance: "supplier_provided" | "customer_supplied" | "canonical_pending" | "missing";
  documentId: string | null;
  documentName: string | null;
  certificationId: string | null;
  certificationTypeId?: string | null;
  certificationName: string | null;
  // ENT-2730 (Codex P1+P2): full evidenced-type set for filter membership, the active
  // subset for coverage-of-the-selected-type, and per-type state so the expiry/customer
  // tiles read per-type rather than the row-global display record. Optional for
  // backward-compatible payloads.
  certificationTypeNames?: string[];
  activeCertificationTypeNames?: string[];
  certificationTypeStates?: Array<{
    name: string;
    active: boolean;
    expiresSoon: boolean;
    customerSupplied: boolean;
  }>;
  certificationCategory: string | null;
  sourceLabel: string;
  hasActiveCertificate?: boolean;
  hasCertificationEvidence?: boolean;
  hasCustomerSuppliedEvidence: boolean;
  auditPeriod: string | null;
  expiresAt: string | null;
  expiresSoon: boolean;
  trustServiceCriteria: string[];
  importantFindingsCount: number | null;
  summary: string | null;
  lastUpdated: string | null;
}

export interface InsightsCertificationSummary {
  totalSupplierCount: number;
  canonicalReadyCount: number;
  customerSuppliedCount: number;
  upcomingExpiryCount?: number;
  externalControlGapCount?: number;
  missingCount: number;
  coveragePercentage: number;
}

export interface InsightsCertificationResponse {
  summary: InsightsCertificationSummary;
  suppliers: InsightsCertificationSupplier[];
  certificationTypes?: string[];
}

export interface InsightsQuestionnaireSupplier {
  supplierId: string;
  supplierName: string;
  vendorProfileId: string | null;
  vendorProfileStatus?: "unclaimed" | "claimed" | "verified" | null;
  marketplaceListingId: string | null;
  websiteUrl: string | null;
  criticalityTier: string | null;
  supplierType: string | null;
  logoUrl: string | null;
  submissionId: string | null;
  submissionVersion: number | null;
  submittedAt: string | null;
  overallPercentage: number | null;
  overallGrade: string | null;
  overallRiskTier: string | null;
  followUpCount: number;
  accessStatus: "shared" | "pending" | "requestable" | "awaiting_supplier";
  hasSubmittedVersion: boolean;
  hasActiveGrant: boolean;
  accessRequestId: string | null;
}

export interface InsightsQuestionnaireOverviewGroup {
  blueprintId: string;
  slug: string;
  title: string;
  topic: string | null;
  aggregateReviewOrder: number;
  defaultFreshnessDays: number;
  supplierCount: number;
  scoredSupplierCount: number;
  averageScore: number | null;
  scoreValues: number[];
}

export interface InsightsQuestionnaireGroup
  extends InsightsQuestionnaireOverviewGroup {
  suppliers: InsightsQuestionnaireSupplier[];
}

export interface InsightsQuestionnaireAggregateSupplier {
  supplierId: string;
  supplierName: string;
  vendorProfileId: string | null;
  vendorProfileStatus?: "unclaimed" | "claimed" | "verified" | null;
  marketplaceListingId: string | null;
  websiteUrl: string | null;
  criticalityTier: string | null;
  supplierType: string | null;
  logoUrl: string | null;
  averageScore: number | null;
  submittedQuestionnaireCount: number;
  sharedQuestionnaireCount: number;
  requestableQuestionnaireCount: number;
  pendingRequestCount: number;
  latestSubmittedAt: string | null;
  overallGrade: string | null;
  actionState: "shared" | "pending" | "requestable" | "invite" | "awaiting_supplier";
}

export interface InsightsQuestionnaireSummary {
  averageScore: number | null;
  submittedSupplierCount: number;
  totalSupplierCount: number;
  questionnaireTypeCount: number;
  scoredSubmissionCount: number;
}

export interface InsightsCustomQuestionnaireSummary {
  averageScore: number | null;
  assessedSupplierCount: number;
  totalSupplierCount: number;
  sentRequestCount: number;
  completedRequestCount: number;
  questionnaireVersionCount: number;
}

export interface InsightsCustomQuestionnaireGroup {
  questionnaireId: string;
  versionId: string;
  versionNumber: number;
  name: string;
  publishedAt: string;
  lifecycleStatus: "draft" | "published" | "archived";
  isLatestPublished: boolean;
  questionCount: number;
  supplierCount: number;
  averageScore: number | null;
  scoreValues: number[];
  latestCompletedAt: string | null;
}

export interface InsightsCustomQuestionnaireAggregateSupplier {
  supplierId: string;
  supplierName: string;
  vendorProfileId: string | null;
  marketplaceListingId: string | null;
  websiteUrl: string | null;
  criticalityTier: string | null;
  supplierType: string | null;
  logoUrl: string | null;
  averageScore: number | null;
  completedQuestionnaireCount: number;
  pendingRequestCount: number;
  latestCompletedAt: string | null;
  latestRequestId: string | null;
  latestVersionId: string | null;
}

export interface InsightsCustomQuestionnaireSupplier {
  requestId: string;
  responseId: string;
  supplierId: string;
  supplierName: string;
  vendorProfileId: string;
  marketplaceListingId: string | null;
  websiteUrl: string | null;
  criticalityTier: string | null;
  supplierType: string | null;
  logoUrl: string | null;
  completedAt: string;
  comparableScore: number;
  grade: string | null;
  scoreCoveragePercentage: number;
  scoringModelKey: string | null;
  scoringModelVersion: string | null;
  scoringContext: Record<string, unknown>;
}

export interface InsightsSelectedCustomQuestionnaire extends InsightsCustomQuestionnaireGroup {
  scoreDistribution: Array<{ minimum: number; maximum: number; count: number }>;
  totalRows: number;
  page: number;
  pageSize: number;
  suppliers: InsightsCustomQuestionnaireSupplier[];
}

export interface InsightsReadModelResponse {
  reportCertificates?: ReportCertificateReadModel;
  insights?: Record<string, unknown>;
  supplierTypes?: string[];
  certifications?: InsightsCertificationResponse;
  vulnerabilities?: {
    domains: DomainVulnerability[];
    emailSubChecks: EmailSubCheck[];
    subChecksByDomain: Record<string, DomainSubCheck[]>;
    totalSuppliers: number;
    supplierSecurityDetails: SupplierSecurityDetails[];
  };
  questionnaires?: {
    summary: InsightsQuestionnaireSummary;
    questionnaires: InsightsQuestionnaireOverviewGroup[];
    aggregateSuppliers: InsightsQuestionnaireAggregateSupplier[];
    selectedQuestionnaire: InsightsQuestionnaireGroup | null;
  };
  customQuestionnaires?: {
    summary: InsightsCustomQuestionnaireSummary;
    questionnaires: InsightsCustomQuestionnaireGroup[];
    aggregateSuppliers: InsightsCustomQuestionnaireAggregateSupplier[];
    selectedQuestionnaire: InsightsSelectedCustomQuestionnaire | null;
  };
}

export interface BreachAlertsReadModelRequest {
  organizationId: string;
}

export interface BreachFeedReadModelRequest {
  organizationId: string;
  supplierSearch?: string;
  activePage?: number;
  activePageSize?: number;
  archivedPage?: number;
  archivedPageSize?: number;
}

export interface AlertCentreReadModelRequest {
  organizationId: string;
  searchQuery?: string;
  activePage?: number;
  activePageSize?: number;
  archivedPage?: number;
  archivedPageSize?: number;
}

export interface CertificationsReadModelRequest {
  organizationId: string;
  statusFilter: string;
  page: number;
  pageSize: number;
}

export interface AlertsReadModelRequest {
  organizationId: string;
  statusFilter: string;
  page: number;
  pageSize: number;
}

export interface TeamReadModelRequest {
  organizationId: string;
  membersPage: number;
  pendingPage: number;
  pageSize: number;
  accessRequestsPage: number;
  accessRequestsPageSize: number;
}

export interface TeamReadModelMember {
  id: string;
  user_id: string;
  role: "admin" | "user" | "customer_service";
  created_at: string;
  is_blocked?: boolean;
  profile?: {
    email: string;
    first_name?: string | null;
    last_name?: string | null;
    avatar_url?: string | null;
  };
  hasMFA?: boolean;
  isMSPStaff?: boolean;
  mspOrgName?: string;
  mspAccessLevel?: "admin" | "staff";
}

export interface TeamReadModelInvitation {
  id: string;
  email: string;
  role: string;
  invited_at: string;
  last_sent_at: string;
  send_count: number;
  status: string;
}

export interface TeamReadModelOrganizationAccessRequest {
  id: string;
  requester_user_id: string;
  requester_email: string;
  normalized_email_domain: string;
  source: 'password_signup' | 'sso';
  sso_duplicate_of_user_id: string | null;
  status: 'pending' | 'approved' | 'declined' | 'cancelled' | 'expired';
  created_at: string;
  decided_at: string | null;
}

export interface TeamReadModelPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

export interface TeamReadModelResponse {
  members: TeamReadModelMember[];
  memberDepartmentAccess: Record<
    string,
    { departmentIds: string[]; hasAll: boolean }
  >;
  membersPagination: TeamReadModelPagination;
  pendingInvitations: TeamReadModelInvitation[];
  pendingInvitationsPagination: TeamReadModelPagination;
  organizationAccessRequests: TeamReadModelOrganizationAccessRequest[];
  organizationAccessRequestsPagination: TeamReadModelPagination;
  mspStaff: TeamReadModelMember[];
  mspOrgName: string | null;
  linkedVendorProfile: {
    id: string;
    domain: string;
    companyName: string;
  } | null;
  stats: {
    totalMembers: number;
    admins: number;
    members: number;
    mspStaff: number;
  };
}

export interface BreachAlertsReadModelArticle {
  id: string;
  title: string;
  url: string;
  description: string;
  matched_supplier: string;
  source_matched_supplier?: string | null;
  supplier_id?: string | null;
  marketplace_listing_id: string | null;
  published_at: string | null;
  source_name: string | null;
  image_url: string | null;
  relevance_score?: number;
  ai_analysis?: string;
}

export interface BreachAlertsReadModelArchivedArticle {
  id: string;
  organization_id: string;
  article_url: string;
  article_title: string;
  article_description: string | null;
  matched_supplier: string;
  source_matched_supplier?: string | null;
  restore_eligible: boolean;
  marketplace_listing_id: string | null;
  published_at: string | null;
  source_name: string | null;
  image_url: string | null;
  relevance_score: number | null;
  ai_analysis: string | null;
  archived_by: string;
  archived_at: string;
  notes: string | null;
}

export interface BreachAlertsReadModelAlert {
  id: string;
  title: string;
  message: string | null;
  action_url: string | null;
  severity: string;
  type: string;
  status: string | null;
  created_at: string;
  supplier_id: string | null;
}

export interface BreachAlertsReadModelResponse {
  suppliers: Array<{ id: string; name: string; status?: string }>;
  activeArticles: BreachAlertsReadModelArticle[];
  archivedArticles: BreachAlertsReadModelArchivedArticle[];
  regularAlerts: BreachAlertsReadModelAlert[];
  lastScanTime: string | null;
  monitoringStatus: Record<string, unknown> | null;
}

export interface BreachFeedPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

export interface BreachFeedReadModelResponse {
  suppliers: Array<{ id: string; name: string; status?: string }>;
  activeArticles: BreachAlertsReadModelArticle[];
  archivedArticles: BreachAlertsReadModelArchivedArticle[];
  lastScanTime: string | null;
  monitoringStatus: Record<string, unknown> | null;
  activePagination: BreachFeedPagination;
  archivedPagination: BreachFeedPagination;
}

export interface AlertCentreReadModelBreachGroup {
  supplier: string;
  supplierId: string;
  articleCount: number;
  maxRelevanceScore: number | null;
  mostRecentDate: string | null;
  articles: BreachAlertsReadModelArticle[];
}

export interface AlertCentreReadModelArchivedGroup {
  supplier: string;
  supplierId: string;
  articleCount: number;
  mostRecentArchivedAt: string | null;
  articles: BreachAlertsReadModelArchivedArticle[];
}

export interface AlertCentreReadModelActiveItem {
  type: "breach_group" | "regular_alert";
  date: string | null;
  payload: AlertCentreReadModelBreachGroup | BreachAlertsReadModelAlert;
}

export interface AlertCentreReadModelResponse {
  activeItems: AlertCentreReadModelActiveItem[];
  archivedGroups: AlertCentreReadModelArchivedGroup[];
  archivedRegularAlerts: BreachAlertsReadModelAlert[];
  summary: {
    activeAlerts: number;
    archivedAlerts: number;
    affectedSuppliers: number;
  };
  activePagination: BreachFeedPagination;
  archivedPagination: BreachFeedPagination;
}

export interface CertificationsReadModelResponse {
  certifications: Array<
    SupplierCertification & {
      supplier: { id: string; name: string };
      certification_type: CertificationType | null;
    }
  >;
  certTypes: CertificationType[];
  stats: {
    total: number;
    verified: number;
    pending: number;
    expired: number;
  };
  pagination: BreachFeedPagination;
}

export interface AlertsReadModelResponse {
  alerts: DashboardAlert[];
  stats: {
    total: number;
    active: number;
    critical: number;
    highPriority: number;
    resolvedToday: number;
  };
  pagination: BreachFeedPagination;
}

async function resolveInvokeError(
  functionName: string,
  error: unknown,
  fallback: string,
  response?: Response,
): Promise<never> {
  throw await buildFunctionInvokeError(functionName, error, fallback, response);
}

const suppliersReadModelInflight = new Map<
  string,
  Promise<SuppliersReadModelResponse>
>();
// ENT-3471: like insights below, the suppliers in-flight map is keyed on the
// request, not the user. OrganizationContext can swap the authenticated user
// WITHOUT a full reload, so a new user issuing the same-org request could
// otherwise join a bounded retry episode started under the previous user's
// headers and receive role/department-filtered rows they are not authorized for.
// The auth listener below clears this map and bumps the generation on every
// authenticated-user change; the retry loop captures the generation at its start
// and aborts (before AND after each invoke) as soon as it changes.
let suppliersAuthGeneration = 0;

interface SuppliersReadModelInvokeOptions {
  /** A committed-write generation that must not join an earlier projection. */
  forceFreshGeneration?: number;
}
const dashboardReadModelInflight = new Map<
  string,
  Promise<DashboardReadModelResponse>
>();
const dashboardSummaryReadModelInflight = new Map<
  string,
  Promise<DashboardSummaryReadModelResponse>
>();
const dashboardDeferredReadModelInflight = new Map<
  string,
  Promise<DashboardDeferredReadModelResponse>
>();
const appBootstrapReadModelInflight = new Map<
  string,
  Promise<AppBootstrapReadModelResponse>
>();
const organizationContextReadModelInflight = new Map<
  string,
  Promise<OrganizationContextReadModelResponse>
>();
const appShellReadModelInflight = new Map<
  string,
  Promise<AppShellReadModelResponse>
>();
const agentFabReadModelInflight = new Map<
  string,
  Promise<AgentFabReadModelResponse>
>();
const breachAlertsReadModelInflight = new Map<
  string,
  Promise<BreachAlertsReadModelResponse>
>();
const breachFeedReadModelInflight = new Map<
  string,
  Promise<BreachFeedReadModelResponse>
>();
const insightsReadModelInflight = new Map<
  string,
  Promise<InsightsReadModelResponse>
>();

// ENT-3312: the insights in-flight map is keyed on the request, not the user.
// OrganizationContext can swap the authenticated user WITHOUT a full reload, so a
// new user issuing the same-org request could otherwise join a promise started
// under the previous user's headers and receive role/department-filtered insights
// they are not authorized for. Drop every in-flight entry whenever the authenticated
// user changes so the new user always starts a fresh, correctly-scoped fetch. The
// abandoned promise resolves to its own cancelled caller and never reaches the UI.
// Guarded with `?.` for test doubles that omit onAuthStateChange.
let lastInsightsAuthUserId: string | null | undefined;
// Bumped on every authenticated-user change. An in-flight retry loop captures the
// generation at its start and aborts as soon as it changes, so a superseded fetch
// stops re-issuing Edge Function calls under the new user's session instead of
// running its remaining attempts to completion.
let insightsAuthGeneration = 0;
supabase.auth?.onAuthStateChange?.((event, session) => {
  // A transient null session (INITIAL_SESSION / TOKEN_REFRESHED / USER_UPDATED emitted
  // while storage is being read — see SessionManager) is NOT a user change. Ignore it
  // so we don't spuriously clear the map, bump the generation, or reject a valid
  // in-flight request. Only a real sign-out or a switch to a different authenticated
  // user invalidates.
  const userId = session?.user?.id ?? null;
  if (userId === null && event !== "SIGNED_OUT") {
    return;
  }
  if (userId !== lastInsightsAuthUserId) {
    lastInsightsAuthUserId = userId;
    insightsAuthGeneration += 1;
    insightsReadModelInflight.clear();
    // ENT-3471: the suppliers read model shares this exact cross-user hazard, so
    // invalidate its in-flight episodes on the same authenticated-user change.
    suppliersAuthGeneration += 1;
    suppliersReadModelInflight.clear();
  }
});
const alertCentreReadModelInflight = new Map<
  string,
  Promise<AlertCentreReadModelResponse>
>();
const superAdminDebugOverlayReadModelInflight = new Map<
  string,
  Promise<SuperAdminDebugOverlayReadModelResponse>
>();
const certificationsReadModelInflight = new Map<
  string,
  Promise<CertificationsReadModelResponse>
>();
const alertsReadModelInflight = new Map<
  string,
  Promise<AlertsReadModelResponse>
>();
const teamReadModelInflight = new Map<string, Promise<TeamReadModelResponse>>();
const navigatorHomeReadModelInflight = new Map<
  string,
  Promise<NavigatorHomeReadModelResponse>
>();
const navigatorSectionSummariesReadModelInflight = new Map<
  string,
  Promise<NavigatorSectionSummariesReadModelResponse>
>();
const navigatorFoundationReadModelInflight = new Map<
  string,
  Promise<NavigatorFoundationReadModelResponse>
>();
const navigatorSuppliersReadModelInflight = new Map<
  string,
  Promise<NavigatorSuppliersReadModelResponse>
>();
const navigatorContractsReadModelInflight = new Map<
  string,
  Promise<NavigatorContractsReadModelResponse>
>();
const navigatorCrownJewelsReadModelInflight = new Map<
  string,
  Promise<NavigatorCrownJewelsReadModelResponse>
>();
const navigatorCharterReadModelInflight = new Map<
  string,
  Promise<NavigatorCharterReadModelResponse>
>();
const navigatorSupplyChainBriefingReadModelInflight = new Map<
  string,
  Promise<NavigatorSupplyChainBriefingReadModelResponse>
>();

type FunctionInvokeResult<TResponse> = Awaited<
  ReturnType<typeof supabase.functions.invoke<TResponse>>
>;

interface ProtectedReadModelOptions {
  expectedUserId?: string;
  accessToken?: string;
  sessionUserId?: string;
  // ENT-4200 (Fix 5b, Codex R2): a caller that must NOT reuse an in-flight request
  // for the same user/request (e.g. a post-SSO-claim bootstrap that has to observe
  // the just-created membership, not a pre-claim request already coalesced under
  // the same key) supplies a distinct generation so it gets its own in-flight slot.
  forceFreshGeneration?: string | number;
}

function buildProtectedReadModelCacheKey(
  request: Record<string, unknown>,
  options: ProtectedReadModelOptions = {},
) {
  return JSON.stringify({
    request,
    expectedUserId: options.expectedUserId ?? null,
    sessionUserId: options.sessionUserId ?? null,
  });
}

async function invokeProtectedReadModel<TResponse>(
  functionName: string,
  body: Record<string, unknown>,
  options: ProtectedReadModelOptions = {},
): Promise<FunctionInvokeResult<TResponse>> {
  return await invokeProtectedFunction<TResponse>(functionName, body, options);
}

export async function invokeSuppliersReadModel(
  request: SuppliersReadModelRequest,
  options: SuppliersReadModelInvokeOptions = {},
): Promise<SuppliersReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflightKey = options.forceFreshGeneration === undefined
    ? cacheKey
    : `${cacheKey}:force-fresh:${options.forceFreshGeneration}`;
  const inflight = suppliersReadModelInflight.get(inflightKey);
  if (inflight) {
    return inflight;
  }

  const startGeneration = suppliersAuthGeneration;
  const requestPromise = (async () => {
    // ENT-3471: coalesce identical callers onto this one bounded retry episode.
    // A short Edge gateway/transport outage therefore self-heals without each
    // caller emitting its own request fan-out, toast, or Sentry event. Typed
    // auth/authorization/entitlement/validation failures remain fail-closed on
    // their first response because the shared classifier does not mark them
    // transient.
    // Pin the session token for this loop's identity. ENT-3471: 403/422 supplier
    // reads fail closed after one Edge invocation and only transport/gateway
    // failures below retry. ENT-5492 (D1): a 401 is still answered inside
    // invokeProtectedFunction by one stored-session retry/refresh — a pinned
    // token that went stale must never end the session by itself.
    const { Authorization } = await getProtectedFunctionHeaders("suppliers-read-model");
    const accessToken = Authorization.slice("Bearer ".length);

    for (let attempt = 0; attempt <= TRANSIENT_READ_MAX_RETRIES; attempt += 1) {
      // Abort a superseded loop: the authenticated user changed since this fetch
      // began, so its remaining attempts would burn Edge Function calls under the
      // new user's session for a caller that has already been cancelled.
      if (suppliersAuthGeneration !== startGeneration) {
        throw new FunctionInvokeError(
          "Suppliers request superseded by an authentication change",
          { functionName: "suppliers-read-model", errorCode: "auth_superseded" },
        );
      }

      const { data, error, response } = await invokeProtectedReadModel<SuppliersReadModelResponse>(
        "suppliers-read-model",
        request,
        { accessToken },
      );

      // Reject a response that completed AFTER an auth-user change: it was fetched
      // under the previous user's token, so returning it would let the old user's
      // role/department-filtered rows commit before the consumer's effect cleanup runs.
      if (suppliersAuthGeneration !== startGeneration) {
        throw new FunctionInvokeError(
          "Suppliers response superseded by an authentication change",
          { functionName: "suppliers-read-model", errorCode: "auth_superseded" },
        );
      }

      if (data && !error) {
        return data;
      }

      if (!isTransientBackendError(error) || attempt === TRANSIENT_READ_MAX_RETRIES) {
        try {
          await resolveInvokeError(
            "suppliers-read-model",
            error,
            "Suppliers read model failed",
            response,
          );
        } catch (terminalError) {
          if (terminalError instanceof FunctionInvokeError) {
            throw new FunctionInvokeError(terminalError.message, {
              functionName: terminalError.functionName,
              status: terminalError.status,
              responseBody: terminalError.responseBody,
              responseJson: terminalError.responseJson,
              errorCode: terminalError.errorCode,
              errorClass: terminalError.errorClass,
              rpcName: terminalError.rpcName,
              requestId: terminalError.requestId,
              attemptCount: attempt + 1,
              isAbort: terminalError.isAbort,
              isFetchError: terminalError.isFetchError,
            });
          }
          throw terminalError;
        }
      }

      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250));
    }

    throw new Error("Unreachable suppliers read-model retry state");
  })().finally(() => {
    // Only evict our own entry. An auth-change clear (above) followed by a new
    // user installing a replacement promise under the same key must survive this
    // settle, or later callers would miss it and restart the retry loop.
    if (suppliersReadModelInflight.get(inflightKey) === requestPromise) {
      suppliersReadModelInflight.delete(inflightKey);
    }
  });

  suppliersReadModelInflight.set(inflightKey, requestPromise);
  return requestPromise;
}

export async function invokeDependenciesReadModel(
  request: DependenciesReadModelRequest,
): Promise<DependenciesReadModelResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    DependenciesReadModelResponse
  >(
    "dependencies-read-model",
    request,
  );

  if (error || !data) {
    await resolveInvokeError(
      "dependencies-read-model",
      error,
      "Dependencies read model failed",
      response,
    );
  }

  return data;
}

export async function invokePoamReadModel(
  request: PoamReadModelRequest,
): Promise<PoamReadModelResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    PoamReadModelResponse
  >(
    "poam-read-model",
    request,
  );

  if (error || !data) {
    await resolveInvokeError(
      "poam-read-model",
      error,
      "POAM read model failed",
      response,
    );
  }

  return data;
}

export async function invokeDashboardReadModel(
  request: DashboardReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<DashboardReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = dashboardReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<DashboardReadModelResponse>(
    "dashboard-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "dashboard-read-model",
        error,
        "Dashboard read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    dashboardReadModelInflight.delete(cacheKey);
  });

  dashboardReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorHomeReadModel(
  request: NavigatorHomeReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorHomeReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorHomeReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorHomeReadModelResponse>(
    "navigator-home-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-home-read-model",
        error,
        "Navigator home read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorHomeReadModelInflight.delete(cacheKey);
  });

  navigatorHomeReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorSectionSummariesReadModel(
  request: NavigatorSectionSummariesReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSectionSummariesReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorSectionSummariesReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = Promise.all([
    invokeNavigatorFoundationReadModel(request, options),
    invokeNavigatorCrownJewelsReadModel(request, options),
    invokeNavigatorSuppliersReadModel(request, options),
    invokeNavigatorContractsReadModel(request, options),
  ]).then(([foundation, crownJewels, suppliers, contracts]) => ({
    foundation: foundation.summary,
    crownJewels: crownJewels.summary,
    suppliers: suppliers.summary,
    contracts: contracts.summary,
  })).finally(() => {
    navigatorSectionSummariesReadModelInflight.delete(cacheKey);
  });

  navigatorSectionSummariesReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorFoundationReadModel(
  request: NavigatorFoundationReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorFoundationReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorFoundationReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorFoundationReadModelResponse>(
    "navigator-foundation-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-foundation-read-model",
        error,
        "Navigator Foundation failed to load",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorFoundationReadModelInflight.delete(cacheKey);
  });

  navigatorFoundationReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorSuppliersReadModel(
  request: NavigatorSuppliersReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSuppliersReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorSuppliersReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorSuppliersReadModelResponse>(
    "navigator-suppliers-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-suppliers-read-model",
        error,
        "Navigator Suppliers failed to load",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorSuppliersReadModelInflight.delete(cacheKey);
  });

  navigatorSuppliersReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorContractsReadModel(
  request: NavigatorContractsReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorContractsReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorContractsReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorContractsReadModelResponse>(
    "navigator-contracts-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-contracts-read-model",
        error,
        "Navigator Contracts failed to load",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorContractsReadModelInflight.delete(cacheKey);
  });

  navigatorContractsReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorSupplyChainBriefingReadModel(
  request: NavigatorSupplyChainBriefingReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSupplyChainBriefingReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorSupplyChainBriefingReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorSupplyChainBriefingReadModelResponse>(
    "navigator-supply-chain-briefing-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-supply-chain-briefing-read-model",
        error,
        "Supply Chain Briefing failed to load",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorSupplyChainBriefingReadModelInflight.delete(cacheKey);
  });

  navigatorSupplyChainBriefingReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function captureNavigatorSupplyChainBriefingSnapshot(
  request: NavigatorSupplyChainBriefingReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSupplyChainBriefingSnapshotResponse> {
  const { data, error, response } = await invokeProtectedReadModel<NavigatorSupplyChainBriefingSnapshotResponse>(
    "navigator-supply-chain-briefing-read-model",
    { ...request, action: "capture_snapshot" },
    options,
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-supply-chain-briefing-read-model",
      error,
      "Supply Chain Briefing snapshot failed to save",
      response,
    );
  }

  return data;
}

export async function listNavigatorSupplyChainBriefingSnapshots(
  request: NavigatorSupplyChainBriefingReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSupplyChainBriefingSnapshotListResponse> {
  const { data, error, response } = await invokeProtectedReadModel<NavigatorSupplyChainBriefingSnapshotListResponse>(
    "navigator-supply-chain-briefing-read-model",
    { ...request, action: "list_snapshots" },
    options,
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-supply-chain-briefing-read-model",
      error,
      "Supply Chain Briefing snapshots failed to load",
      response,
    );
  }

  return data;
}

export async function getNavigatorSupplyChainBriefingSnapshot(
  request: NavigatorSupplyChainBriefingReadModelRequest & { snapshotId: string },
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSupplyChainBriefingSnapshotResponse> {
  const { data, error, response } = await invokeProtectedReadModel<NavigatorSupplyChainBriefingSnapshotResponse>(
    "navigator-supply-chain-briefing-read-model",
    { ...request, action: "get_snapshot" },
    options,
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-supply-chain-briefing-read-model",
      error,
      "Supply Chain Briefing snapshot failed to load",
      response,
    );
  }

  return data;
}

export async function getNavigatorSupplyChainBriefingSnapshotArtifactUrl(
  request: NavigatorSupplyChainBriefingReadModelRequest & { snapshotId: string; artifactType: "html" | "pdf" },
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorSupplyChainBriefingSnapshotArtifactUrlResponse> {
  const { data, error, response } = await invokeProtectedReadModel<NavigatorSupplyChainBriefingSnapshotArtifactUrlResponse>(
    "navigator-supply-chain-briefing-read-model",
    { ...request, action: "get_snapshot_artifact_url" },
    options,
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-supply-chain-briefing-read-model",
      error,
      "Supply Chain Briefing snapshot artifact link could not be prepared",
      response,
    );
  }

  return data;
}

export async function invokeNavigatorCrownJewelsReadModel(
  request: NavigatorCrownJewelsReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCrownJewelsReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorCrownJewelsReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorCrownJewelsReadModelResponse>(
    "navigator-crown-jewels-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-crown-jewels-read-model",
        error,
        "Navigator Crown Jewels failed to load",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorCrownJewelsReadModelInflight.delete(cacheKey);
  });

  navigatorCrownJewelsReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorCrownJewelsUpsert(
  request: NavigatorCrownJewelsUpsertRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCrownJewelsUpsertResponse> {
  const headers = await getProtectedFunctionHeaders("navigator-crown-jewels-upsert", options);
  const { data, error, response } = await supabase.functions.invoke<NavigatorCrownJewelsUpsertResponse>(
    "navigator-crown-jewels-upsert",
    {
      body: request,
      headers,
    },
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-crown-jewels-upsert",
      error,
      "Navigator Crown Jewels failed to save",
      response,
    );
  }

  return data;
}

export async function invokeNavigatorCrownJewelLinkageUpsert(
  request: NavigatorCrownJewelLinkageUpsertRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCrownJewelLinkageUpsertResponse> {
  const headers = await getProtectedFunctionHeaders("navigator-crown-jewel-linkage-upsert", options);
  const { data, error, response } = await supabase.functions.invoke<NavigatorCrownJewelLinkageUpsertResponse>(
    "navigator-crown-jewel-linkage-upsert",
    {
      body: request,
      headers,
    },
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-crown-jewel-linkage-upsert",
      error,
      "Navigator Crown Jewel linkage failed to save",
      response,
    );
  }

  return data;
}

export async function invokeNavigatorFoundationUpsert(
  request: NavigatorFoundationUpsertRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorFoundationUpsertResponse> {
  const headers = await getProtectedFunctionHeaders("navigator-foundation-upsert", options);
  const { data, error, response } = await supabase.functions.invoke<NavigatorFoundationUpsertResponse>(
    "navigator-foundation-upsert",
    {
      body: request,
      headers,
    },
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-foundation-upsert",
      error,
      "Navigator Foundation failed to save",
      response,
    );
  }

  return data;
}

export async function invokeNavigatorCharterReadModel(
  request: NavigatorCharterReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCharterReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = navigatorCharterReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<NavigatorCharterReadModelResponse>(
    "navigator-charter-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "navigator-charter-read-model",
        error,
        "Navigator charter failed to load",
        response,
      );
    }

    return data;
  }).finally(() => {
    navigatorCharterReadModelInflight.delete(cacheKey);
  });

  navigatorCharterReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeNavigatorCharterGenerate(
  request: NavigatorCharterGenerateRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCharterGenerateResponse> {
  const headers = await getProtectedFunctionHeaders("navigator-charter-generate", options);
  const { data, error, response } = await supabase.functions.invoke<NavigatorCharterGenerateResponse>(
    "navigator-charter-generate",
    { body: request, headers },
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-charter-generate",
      error,
      "Navigator charter generation failed",
      response,
    );
  }

  return data;
}

export async function invokeNavigatorCharterSaveDraft(
  request: NavigatorCharterSaveDraftRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCharterSaveDraftResponse> {
  const headers = await getProtectedFunctionHeaders("navigator-charter-save-draft", options);
  const { data, error, response } = await supabase.functions.invoke<NavigatorCharterSaveDraftResponse>(
    "navigator-charter-save-draft",
    { body: request, headers },
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-charter-save-draft",
      error,
      "Navigator charter draft failed to save",
      response,
    );
  }

  return data;
}

export async function invokeNavigatorCharterPublish(
  request: NavigatorCharterPublishRequest,
  options: ProtectedReadModelOptions = {},
): Promise<NavigatorCharterPublishResponse> {
  const headers = await getProtectedFunctionHeaders("navigator-charter-publish", options);
  const { data, error, response } = await supabase.functions.invoke<NavigatorCharterPublishResponse>(
    "navigator-charter-publish",
    { body: request, headers },
  );

  if (error || !data) {
    await resolveInvokeError(
      "navigator-charter-publish",
      error,
      "Navigator charter publish failed",
      response,
    );
  }

  return data;
}

export async function invokeDashboardSummaryReadModel(
  request: DashboardReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<DashboardSummaryReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = dashboardSummaryReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<
    DashboardSummaryReadModelResponse
  >(
    "dashboard-summary-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "dashboard-summary-read-model",
        error,
        "Dashboard summary read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    dashboardSummaryReadModelInflight.delete(cacheKey);
  });

  dashboardSummaryReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeDashboardDeferredReadModel(
  request: DashboardReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<DashboardDeferredReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = dashboardDeferredReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<
    DashboardDeferredReadModelResponse
  >(
    "dashboard-deferred-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "dashboard-deferred-read-model",
        error,
        "Dashboard deferred read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    dashboardDeferredReadModelInflight.delete(cacheKey);
  });

  dashboardDeferredReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeMspCustomersDashboard(
  request: MspCustomersDashboardRequest,
): Promise<MspCustomersDashboardResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    MspCustomersDashboardResponse
  >(
    "msp-customers-dashboard",
    request,
  );

  if (error || !data) {
    await resolveInvokeError(
      "msp-customers-dashboard",
      error,
      "MSP customers dashboard failed",
      response,
    );
  }

  return data;
}

export async function invokeMspInsightsDashboard(
  request: MspInsightsDashboardRequest,
): Promise<MspInsightsDashboardResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    MspInsightsDashboardResponse
  >(
    "msp-insights-dashboard",
    request,
  );

  if (error || !data) {
    await resolveInvokeError(
      "msp-insights-dashboard",
      error,
      "MSP insights dashboard failed",
      response,
    );
  }

  return data;
}

export async function invokeAppShellReadModel(
  request: AppShellReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<AppShellReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = appShellReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<AppShellReadModelResponse>(
    "app-shell-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "app-shell-read-model",
        error,
        "App shell read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    appShellReadModelInflight.delete(cacheKey);
  });

  appShellReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeAgentFabReadModel(
  request: AgentFabReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<AgentFabReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = agentFabReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<AgentFabReadModelResponse>(
    "agent-fab-read-model",
    request,
    options,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "agent-fab-read-model",
        error,
        "Agent FAB read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    agentFabReadModelInflight.delete(cacheKey);
  });

  agentFabReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeAdminPlatformInsightsReadModel(): Promise<
  AdminPlatformInsightsResponse
> {
  const { data, error, response } = await invokeProtectedReadModel<
    AdminPlatformInsightsResponse
  >(
    "admin-platform-insights",
    {},
  );

  if (error || !data) {
    await resolveInvokeError(
      "admin-platform-insights",
      error,
      "Admin platform insights failed",
      response,
    );
  }

  return data;
}

export async function invokeAdminMarketplaceDependenciesReadModel(
  request: AdminMarketplaceDependenciesRequest,
): Promise<AdminMarketplaceDependenciesResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    AdminMarketplaceDependenciesResponse
  >(
    "admin-marketplace-dependencies",
    request,
  );

  if (error || !data) {
    await resolveInvokeError(
      "admin-marketplace-dependencies",
      error,
      "Admin marketplace dependencies failed",
      response,
    );
  }

  return data;
}

/**
 * ENT-6287: super_admin maps a provider name to its listing's domain (the
 * "Resolve" / "Add catalogue row" actions on Marketplace → Dependencies). The
 * database trigger relinks every row with that name in the same transaction.
 */
export async function invokeAdminProviderCatalogUpsert(
  request: AdminProviderCatalogUpsertRequest,
): Promise<AdminProviderCatalogUpsertResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    AdminProviderCatalogUpsertResponse
  >(
    "admin-marketplace-dependencies",
    { action: "upsert_catalog", ...request },
  );

  if (error || !data) {
    await resolveInvokeError(
      "admin-marketplace-dependencies",
      error,
      "Saving the provider catalogue row failed",
      response,
    );
  }

  return data;
}

export async function invokeAdminCronMonitoringSummary(): Promise<
  AdminCronMonitoringSummaryResponse
> {
  const { data, error, response } = await invokeProtectedReadModel<
    AdminCronMonitoringSummaryResponse
  >(
    "admin-cron-monitoring",
    {},
  );

  if (error || !data) {
    await resolveInvokeError(
      "admin-cron-monitoring",
      error,
      "Admin cron monitoring summary failed",
      response,
    );
  }

  return data;
}

export async function invokeAdminCronMonitoringDetail(
  jobKey: string,
  refresh = false,
): Promise<AdminCronMonitoringDetailResponse> {
  const { data, error, response } = await invokeProtectedReadModel<
    AdminCronMonitoringDetailResponse
  >(
    "admin-cron-monitoring",
    { jobKey, refresh },
  );

  if (error || !data) {
    await resolveInvokeError(
      "admin-cron-monitoring",
      error,
      "Admin cron monitoring detail failed",
      response,
    );
  }

  return data;
}

export async function invokeAdminThirdPartyApiBudgets(): Promise<AdminThirdPartyApiBudgetsResponse> {
  const { data, error, response } = await invokeProtectedFunction<AdminThirdPartyApiBudgetsResponse>(
    'admin-third-party-api-budgets',
    undefined,
    { method: 'GET' },
  );

  if (error || !data) {
    await resolveInvokeError(
      'admin-third-party-api-budgets',
      error,
      'Admin third-party API budgets failed',
      response,
    );
  }

  return data;
}

export async function updateAdminThirdPartyApiBudgets(request: {
  mode: AdminThirdPartyApiBudgetMode;
  providers: Array<{
    provider: AdminThirdPartyApiBudgetProviderState['provider'];
    consumerKey?: AdminThirdPartyApiBudgetProviderState['consumerKey'];
    enabled: boolean;
    window: AdminThirdPartyApiBudgetProviderState['window'];
    maxCalls: number;
    metricType?: AdminThirdPartyApiBudgetProviderState['metricType'];
    limitValue?: number;
  }>;
}): Promise<AdminThirdPartyApiBudgetsResponse> {
  const { data, error, response } = await invokeProtectedFunction<AdminThirdPartyApiBudgetsResponse>(
    'admin-third-party-api-budgets',
    {
      mode: request.mode,
      providers: request.providers,
    },
  );

  if (error || !data) {
    await resolveInvokeError(
      'admin-third-party-api-budgets',
      error,
      'Updating admin third-party API budgets failed',
      response,
    );
  }

  return data;
}

export async function invokeInsightsReadModel(
  request: InsightsReadModelRequest,
): Promise<InsightsReadModelResponse> {
  // ENT-3312: the insights read model backs every /insights section and a single
  // Edge Function 502/503 previously hard-failed the whole overview (ENT-3307 platform
  // incident). Coalesce concurrent identical requests onto ONE in-flight promise —
  // exactly like the organization-context and breach-feed read models above — so the
  // sidebar hover-prefetch, the mounted route, and any remount all share a single
  // retry sequence instead of each firing their own (which multiplied Edge Function
  // calls during an outage). Retry only classified transport failures inside that
  // shared promise; authorization and entitlement denials are not transient, so they
  // surface on the first response and stay fail-closed.
  // Coalesce overview requests whether or not the caller passes the explicit
  // default `section: 'overview'` — the Edge Function treats an absent section as
  // overview, so the sidebar prefetch (which sends `section: 'overview'`) and the
  // mounted route (which omits it) are the same request and must share one loop.
  const cacheKey = JSON.stringify({ ...request, section: request.section ?? "overview" });
  const inflight = insightsReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const startGeneration = insightsAuthGeneration;
  const requestPromise = (async () => {
    for (let attempt = 0; attempt <= TRANSIENT_READ_MAX_RETRIES; attempt += 1) {
      // Abort a superseded loop: the authenticated user changed since this fetch
      // began, so its remaining attempts would burn Edge Function calls under the
      // new user's session for a caller that has already been cancelled.
      if (insightsAuthGeneration !== startGeneration) {
        throw new FunctionInvokeError(
          "Insights request superseded by an authentication change",
          { functionName: "insights-read-model", errorCode: "auth_superseded" },
        );
      }

      const { data, error, response } = await invokeProtectedReadModel<
        InsightsReadModelResponse
      >(
        "insights-read-model",
        request,
      );

      // Reject a response that completed AFTER an auth-user change: it was fetched
      // under the previous user's token, so returning it would let the old user's
      // role/department-filtered data commit before the consumer's effect cleanup runs.
      if (insightsAuthGeneration !== startGeneration) {
        throw new FunctionInvokeError(
          "Insights response superseded by an authentication change",
          { functionName: "insights-read-model", errorCode: "auth_superseded" },
        );
      }

      if (data && !error) {
        return data;
      }

      if (!isTransientBackendError(error) || attempt === TRANSIENT_READ_MAX_RETRIES) {
        await resolveInvokeError(
          "insights-read-model",
          error,
          "Insights read model failed",
          response,
        );
      }

      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250));
    }

    throw new Error("Unreachable insights read-model retry state");
  })().finally(() => {
    // Only evict our own entry. An auth-change clear (below) followed by a new
    // user installing a replacement promise under the same key must survive this
    // settle, or later callers would miss it and restart the retry loop.
    if (insightsReadModelInflight.get(cacheKey) === requestPromise) {
      insightsReadModelInflight.delete(cacheKey);
    }
  });

  insightsReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeOrganizationContextReadModel(
  request: OrganizationContextReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<OrganizationContextReadModelResponse> {
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflight = organizationContextReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = (async () => {
    // This hydration starts immediately after sign-in, while the browser can
    // still be establishing its first Edge Function connection. Retry only
    // classified transport failures, but preserve all authorization and
    // entitlement denials exactly as the backend returned them.
    for (let attempt = 0; attempt <= TRANSIENT_READ_MAX_RETRIES; attempt += 1) {
      const { data, error, response } = await invokeProtectedReadModel<
        OrganizationContextReadModelResponse
      >(
        "organization-context-read-model",
        request,
        options,
      );

      if (data && !error) {
        return data;
      }

      if (!isTransientBackendError(error) || attempt === TRANSIENT_READ_MAX_RETRIES) {
        await resolveInvokeError(
          "organization-context-read-model",
          error,
          "Organisation context read model failed",
          response,
        );
      }

      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250));
    }

    throw new Error("Unreachable organisation context read-model retry state");
  })().finally(() => {
    organizationContextReadModelInflight.delete(cacheKey);
  });

  organizationContextReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeAppBootstrapReadModel(
  request: AppBootstrapReadModelRequest,
  options: ProtectedReadModelOptions = {},
): Promise<AppBootstrapReadModelResponse> {
  // ENT-4200 (Fix 5b, Codex R2 P2): a forced post-claim bootstrap must not be
  // coalesced onto a pre-claim request already in flight for the same user (its
  // key omits any force generation), or it would await that request's stale
  // pre-membership snapshot. `forceFreshGeneration` gives the forced call its own
  // in-flight slot so it always issues a fresh request.
  const cacheKey = buildProtectedReadModelCacheKey(request, options);
  const inflightKey = options.forceFreshGeneration === undefined
    ? cacheKey
    : `${cacheKey}:force-fresh:${options.forceFreshGeneration}`;
  const inflight = appBootstrapReadModelInflight.get(inflightKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = (async () => {
    // The first bootstrap races the browser's initial Edge Function connection.
    // Retry transport-only failures so a vendor-team-only session reaches its
    // canonical assurance read without emitting a spurious bootstrap error.
    for (let attempt = 0; attempt <= TRANSIENT_READ_MAX_RETRIES; attempt += 1) {
      const { data, error, response } = await invokeProtectedReadModel<
        AppBootstrapReadModelResponse
      >(
        "app-bootstrap-read-model",
        request,
        options,
      );

      if (data && !error) {
        return data;
      }

      if (!isTransientBackendError(error) || attempt === TRANSIENT_READ_MAX_RETRIES) {
        await resolveInvokeError(
          "app-bootstrap-read-model",
          error,
          "App bootstrap read model failed",
          response,
        );
      }

      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250));
    }

    throw new Error("Unreachable app bootstrap read-model retry state");
  })().finally(() => {
    appBootstrapReadModelInflight.delete(inflightKey);
  });

  appBootstrapReadModelInflight.set(inflightKey, requestPromise);
  return requestPromise;
}

export async function invokeBreachAlertsReadModel(
  request: BreachAlertsReadModelRequest,
): Promise<BreachAlertsReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = breachAlertsReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<
    BreachAlertsReadModelResponse
  >(
    "breach-alerts-read-model",
    request,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "breach-alerts-read-model",
        error,
        "Breach alerts read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    breachAlertsReadModelInflight.delete(cacheKey);
  });

  breachAlertsReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeBreachFeedReadModel(
  request: BreachFeedReadModelRequest,
): Promise<BreachFeedReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = breachFeedReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = (async () => {
    // The feed loads immediately after sign-in, when an Edge Function transport
    // can still be warming. Retry only classified transport failures; backend
    // authorization and entitlement errors remain fail-closed on the first response.
    for (let attempt = 0; attempt <= TRANSIENT_READ_MAX_RETRIES; attempt += 1) {
      const { data, error, response } = await invokeProtectedReadModel<BreachFeedReadModelResponse>(
        "breach-feed-read-model",
        request,
      );

      if (data && !error) {
        return data;
      }

      if (!isTransientBackendError(error) || attempt === TRANSIENT_READ_MAX_RETRIES) {
        await resolveInvokeError(
          "breach-feed-read-model",
          error,
          "Breach feed read model failed",
          response,
        );
      }

      await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250));
    }

    throw new Error("Unreachable breach-feed read-model retry state");
  })().finally(() => {
    breachFeedReadModelInflight.delete(cacheKey);
  });

  breachFeedReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeAlertCentreReadModel(
  request: AlertCentreReadModelRequest,
): Promise<AlertCentreReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = alertCentreReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<AlertCentreReadModelResponse>(
    "alert-centre-read-model",
    request,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "alert-centre-read-model",
        error,
        "Alert centre read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    alertCentreReadModelInflight.delete(cacheKey);
  });

  alertCentreReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeSuperAdminDebugOverlayReadModel(
  request: SuperAdminDebugOverlayReadModelRequest,
): Promise<SuperAdminDebugOverlayReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = superAdminDebugOverlayReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<SuperAdminDebugOverlayReadModelResponse>(
    "super-admin-debug-overlay-read-model",
    request,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "super-admin-debug-overlay-read-model",
        error,
        "Super admin debug overlay read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    superAdminDebugOverlayReadModelInflight.delete(cacheKey);
  });

  superAdminDebugOverlayReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeCertificationsReadModel(
  request: CertificationsReadModelRequest,
): Promise<CertificationsReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = certificationsReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<
    CertificationsReadModelResponse
  >(
    "certifications-read-model",
    request,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "certifications-read-model",
        error,
        "Certifications read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    certificationsReadModelInflight.delete(cacheKey);
  });

  certificationsReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeAlertsReadModel(
  request: AlertsReadModelRequest,
): Promise<AlertsReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = alertsReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<AlertsReadModelResponse>(
    "alerts-read-model",
    request,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "alerts-read-model",
        error,
        "Alerts read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    alertsReadModelInflight.delete(cacheKey);
  });

  alertsReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

export async function invokeTeamReadModel(
  request: TeamReadModelRequest,
): Promise<TeamReadModelResponse> {
  const cacheKey = JSON.stringify(request);
  const inflight = teamReadModelInflight.get(cacheKey);
  if (inflight) {
    return inflight;
  }

  const requestPromise = invokeProtectedReadModel<TeamReadModelResponse>(
    "team-read-model",
    request,
  ).then(async ({ data, error, response }) => {
    if (error || !data) {
      await resolveInvokeError(
        "team-read-model",
        error,
        "Team read model failed",
        response,
      );
    }

    return data;
  }).finally(() => {
    teamReadModelInflight.delete(cacheKey);
  });

  teamReadModelInflight.set(cacheKey, requestPromise);
  return requestPromise;
}
